import { useShallow } from 'zustand/react/shallow';

import {
  claimLeaderboardAllPrizes,
  getLeaderboardMarketItems,
  getLeaderboards,
  getLeaderboardsTop,
  getLeaderboardTasks,
  getLeaderboardUsers,
  getLeaderboardUsersByType,
} from '@/shared/api/leaderboards';
import {
  getCloudStorageValue,
  setCloudStorageValue,
} from '@/shared/helpers/cloud-storage';
import { createClearableStore } from '@/shared/helpers/create-clearable-store';
import { makePageableResult } from '@/shared/helpers/make-pageable-result';
import { type PageableOptions } from '@/shared/types/common';
import {
  IS_FAV_GAMES_LB_BANNER,
  IS_FULL_REFERRAL_BANNER_IN_LB,
} from '@/shared/ui/banner/constants';

import { normalizeLeaderboard } from '../helpers';
import {
  type LeaderboardContestType,
  type LeaderboardsState,
  type LeaderboardsStore,
  type UserLeaderboard,
} from './types';

const defaultLootboxState: LeaderboardsState = {
  topWinners: undefined,
  topLeadersPDFavGamesLB: undefined,
  topLeadersDailyGameLB: undefined,
  leaderboards: undefined,
  usersByLeaderboardId: {},
  showFullReferralBanner: null,
  showFavGamesBanner: null,
  leaderboardMarketItemsDict: {},
  leaderboardTasks: {},
};

export const useLeaderboardsStateRaw = createClearableStore<LeaderboardsStore>()(
  (set) => ({
    ...defaultLootboxState,
    actions: {
      fetchLeaderboardsTop: async () => {
        try {
          const topWinners = await getLeaderboardsTop();

          set({ topWinners });

          return topWinners;
        } catch (err) {
          console.error('ERROR in fetchLeaderboardsTop', err);
        }
      },
      fetchLeaderboards: async () => {
        try {
          const leaderboards = Object.values((await getLeaderboards()) ?? {}).flat();

          set({
            leaderboards: leaderboards.map(normalizeLeaderboard),
          });

          return leaderboards;
        } catch (err) {
          console.error('ERROR in fetchLeaderboards', err);
        }
      },
      fetchUsersByLeaderboard: async (id, options?: PageableOptions) => {
        try {
          const pageableLeaderboardUsers = await getLeaderboardUsers(id, options);

          const { content, pageable } = pageableLeaderboardUsers ?? {};

          set((prev) => ({
            usersByLeaderboardId: {
              ...(prev.usersByLeaderboardId ?? {}),
              [id]: makePageableResult((prev.usersByLeaderboardId ?? {})[id], {
                content,
                pageable,
              }),
            },
          }));

          return pageableLeaderboardUsers;
        } catch (err) {
          console.error('ERROR in fetchLeaderboardUsers', err);
          throw err;
        }
      },
      fetchUsersOfLeaderboardByType: async (
        type: LeaderboardContestType,
        options?: PageableOptions
      ) => {
        try {
          const pageableLeaderboardUsers = await getLeaderboardUsersByType(type, options);

          return pageableLeaderboardUsers;
        } catch (err) {
          console.error('ERROR in fetchLeaderboardUsersByType', err);
        }
      },
      setReferralBannerVisibility: async () => {
        const isReferralBannerVisible = await getCloudStorageValue(
          IS_FULL_REFERRAL_BANNER_IN_LB
        );

        if (isReferralBannerVisible === null) {
          set({ showFullReferralBanner: true });
          await setCloudStorageValue(IS_FULL_REFERRAL_BANNER_IN_LB, 'true');
        } else {
          set({ showFullReferralBanner: isReferralBannerVisible === 'true' });
        }
      },
      setFavGamesBannerVisibility: async () => {
        const isFavGamesBannerVisible =
          await getCloudStorageValue(IS_FAV_GAMES_LB_BANNER);

        if (isFavGamesBannerVisible === null) {
          set({ showFavGamesBanner: true });
          await setCloudStorageValue(IS_FAV_GAMES_LB_BANNER, 'true');
        } else {
          set({ showFavGamesBanner: isFavGamesBannerVisible === 'true' });
        }
      },
      setLeadersPDFavGamesLB: async (users: UserLeaderboard[]) => {
        set({ topLeadersPDFavGamesLB: users });
      },
      setLeadersDailyGameLB: async (users: UserLeaderboard[]) => {
        set({ topLeadersDailyGameLB: users });
      },

      fetchLeaderboardMarketItems: async (leaderboardId) => {
        try {
          const marketItems = await getLeaderboardMarketItems(leaderboardId);

          set((prev) => ({
            leaderboardMarketItemsDict: {
              ...prev.leaderboardMarketItemsDict,
              [leaderboardId]: marketItems,
            },
          }));

          return marketItems;
        } catch (err) {
          console.error('ERROR in fetchLeaderboardMarketItems', err);
        }
      },

      fetchLeaderboardTasks: async (tasksGroup: string) => {
        try {
          const data = await getLeaderboardTasks(tasksGroup);

          const taskGroups = data?.categories;
          const allTasks = taskGroups.flatMap((group) => group.content?.tasks ?? []);

          set((prev) => ({
            leaderboardTasks: {
              ...prev.leaderboardTasks,
              [tasksGroup]: allTasks,
            },
          }));

          return data;
        } catch (err) {
          console.error('ERROR in fetchLeaderboardTasks', err);
        }
      },
      grabLeaderboardsAllPrizes: async (leaderboardId: number) => {
        try {
          await claimLeaderboardAllPrizes(leaderboardId);
        } catch (err) {
          console.error(`ERROR in grab all prizes in leaderboard ${leaderboardId}`, err);
        }
      },
    },
  })
);

export const useTopWinners = () => {
  return useLeaderboardsStateRaw(useShallow((state) => state.topWinners));
};

export const useLeaderboards = () => {
  return useLeaderboardsStateRaw(useShallow((state) => state.leaderboards));
};

export const useUsersByLeaderboard = (id: number) =>
  useLeaderboardsStateRaw(useShallow((state) => state.usersByLeaderboardId[id]));

export const useLeaderboardsActions = () =>
  useLeaderboardsStateRaw((state) => state.actions);

export const useShowFullReferralBannerVisible = () => {
  return useLeaderboardsStateRaw(useShallow((state) => state.showFullReferralBanner));
};

export const useShowFavGamesBanner = () => {
  return useLeaderboardsStateRaw(useShallow((state) => state.showFavGamesBanner));
};

export const useLeadersPDFavGamesLB = () =>
  useLeaderboardsStateRaw((state) => state.topLeadersPDFavGamesLB);

export const useLeadersDailyGameLB = () =>
  useLeaderboardsStateRaw((state) => state.topLeadersDailyGameLB);

export const useLeaderboardMarketItems = (leaderboardId: number | undefined) => {
  return useLeaderboardsStateRaw(
    useShallow((state) =>
      typeof leaderboardId !== 'undefined'
        ? state.leaderboardMarketItemsDict[leaderboardId]
        : undefined
    )
  );
};

export const useLeaderboardTasks = (tasksGroup: string | null) => {
  return useLeaderboardsStateRaw(
    useShallow((state) =>
      tasksGroup != null ? state.leaderboardTasks[tasksGroup] : undefined
    )
  );
};
